import React, { useEffect, useRef, useState } from 'react';
import SubjectIcon from '@material-ui/icons/Subject';
import Card from '../../../common/card/Card';
import CardTitle from '../../../common/card/CardTitle';
import {
  BaseChangeOrderCardProps,
  ChangeOrder
} from '../ChangeOrder.interface';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineTextField from '../../../common/inline/InlineTextField';
import InlineFormik from '../../../common/inline/InlineFormik';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import InlineDatePicker from '../../../common/inline/InlineDatePicker';
import SingleUserSelector from '../../users/components/SingleUserSelector';
import { InlineFormControl } from '../../../common/inline/InlineFormControl';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import { User } from '../../../user/User.interface';
import { useUpdateChangeOrder } from '../ChangeOrder.hook';
import InlineMultiSelect, {
  InlineMultiSelectOption
} from '../../../common/inline/InlineMultiSelect';
import InlineSelect from '../../../common/inline/InlineSelect';
import InlineHtml from '../../../common/inline/InlineHtml';
import { useUpdateTask } from '../../tasks/Task.hooks';
import environment from '../../../environments';

const useMarsbioForm = environment.changeOrderConfig?.useMarsbioForm;

export interface ChangeOrderDetailsCardProps extends BaseChangeOrderCardProps {
  changeOrder: ChangeOrder;
  useCustomfields?: boolean;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
}

export const TypeOfChangeOptions: InlineMultiSelectOption[] = [
  { value: 'product', label: 'Product' },
  { value: 'labeling', label: 'Labeling' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'supplier', label: 'Supplier' }
];

if (!useMarsbioForm) {
  TypeOfChangeOptions.push(
    {
      value: 'quality/regulatory document',
      label: 'Quality/Regulatory Document'
    },
    { value: 'IT assets/IT access', label: 'IT Assets/IT Access' },
    { value: 'distribution', label: 'Distribution' }
  );
}

TypeOfChangeOptions.push({ value: 'other', label: 'Other (Please describe)' });

export const ChangeOrderDetailsCard: React.FC<
  ChangeOrderDetailsCardProps
> = props => {
  const {
    changeOrder,
    useCustomfields,
    onChange,
    setSuccessMessage,
    setErrorMessage
  } = props;
  const [currentRequestor, setCurrentRequestor] = useState<number | null>(
    changeOrder.requestor?.id || null
  );
  const onChangeRef = useRef(onChange);
  const { updatedChangeOrder, isLoading, error, updateChangeOrder } =
    useUpdateChangeOrder(changeOrder.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(changeOrder.task.id || 0);
  const { isFieldDisabled } = useTaskEntityFields(changeOrder.task);

  useEffect(() => {
    if (updatedChangeOrder?.id) {
      setSuccessMessage('Change Order successfully updated');
      onChangeRef.current?.(updatedChangeOrder);
    }
  }, [setSuccessMessage, updatedChangeOrder]);

  useEffect(() => {
    if (Boolean(error || updateTaskError))
      setErrorMessage('There was an error saving');
  }, [setErrorMessage, error, updateTaskError]);

  useEffect(() => {
    if (updatedTask) {
      changeOrder.task = Object.assign(changeOrder.task || {}, updatedTask);
      onChange?.(changeOrder);
    }
  }, [updatedTask]);

  const handleOnChangeAttachment = (attachments: InlineFileValue[]) => {
    updateTask({ attachments });
    if (changeOrder.task) {
      changeOrder.task.attachments = attachments;
    }
    onChange?.(changeOrder);
  };

  return (
    <InlineFormik
      initialValues={
        {
          ...changeOrder,
          urgentChange: changeOrder.urgentChange || '',
          typeOfChange: changeOrder.typeOfChange || [],
          description: changeOrder.description || '',
          reasonForChange: changeOrder.reasonForChange || '',
          affectedParts: changeOrder.affectedParts || '',
          impactOfChange: changeOrder.impactOfChange || '',
          requestor: changeOrder.requestor?.id,
          changeToTrainingMatrix: changeOrder.changeToTrainingMatrix || '',
          comments: changeOrder.comments || ''
        } as any
      }
      strict={false}
      isSubmitting={isLoading}
      error={error}
      onSubmit={(updatedValue: Partial<ChangeOrder>) => {
        const dto = { ...updatedValue };
        // allow removing of date
        if ((dto as any).requestDate === '') {
          (dto as any).requestDate = null;
        }
        updateChangeOrder(dto);
      }}
    >
      {({ setFieldValue, submitForm }) => (
        <Card>
          <CardTitle>
            <SubjectIcon />
            Details
          </CardTitle>
          <CardContent padding='large'>
            <InlineForm>
              <InlineTextField
                name='name'
                label={useCustomfields ? 'Title' : 'Name'}
                helperText={useCustomfields ? 'Title of the change' : ''}
                disabled={isFieldDisabled('name')}
              />
              {useCustomfields ? (
                <>
                  <InlineMultiSelect
                    name='typeOfChange'
                    label='Type of Change'
                    disabled={isFieldDisabled('typeOfChange')}
                    options={TypeOfChangeOptions}
                    helperText='Check one or more types of changes'
                  />
                  {useMarsbioForm && (
                    <InlineSelect
                      name='urgentChange'
                      label='Urgent Change'
                      disabled={isFieldDisabled('urgentChange')}
                      options={[
                        { value: 'yes', label: 'Yes' },
                        { value: 'no', label: 'No' }
                      ]}
                    />
                  )}
                  <InlineHtml
                    name='description'
                    label='Description of Change'
                    disabled={isFieldDisabled('description')}
                    helperText={
                      useMarsbioForm
                        ? 'Describe the change and attach draft document changes as needed,'
                        : 'Describe the change and attach redlines as needed'
                    }
                    attachments={changeOrder.task.attachments || []}
                    onChangeAttachments={handleOnChangeAttachment}
                  />
                  <InlineHtml
                    name='reasonForChange'
                    label='Reason for Change'
                    disabled={isFieldDisabled('reasonForChange')}
                    helperText='Describe the reason for change'
                    attachments={changeOrder.task.attachments || []}
                    onChangeAttachments={handleOnChangeAttachment}
                  />
                  {useMarsbioForm && (
                    <InlineHtml
                      name='affectedParts'
                      label='Affected Parts'
                      disabled={isFieldDisabled('affectedParts')}
                      helperText='List the affected assemblies, parts, software, product documentation & labelling, QMS process etc.'
                      attachments={changeOrder.task.attachments || []}
                      onChangeAttachments={handleOnChangeAttachment}
                    />
                  )}
                </>
              ) : (
                <InlineTextField
                  name='description'
                  label='Description'
                  disabled={isFieldDisabled('description')}
                />
              )}

              {useCustomfields ? (
                <>
                  {/* Hide due to duplication */}
                  {/* <InlineSelect
                    name='changeToTrainingMatrix'
                    label='Change to Training Matrix?'
                    disabled={isFieldDisabled('changeToTrainingMatrix')}
                    options={[
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No' }
                    ]}
                    helperText='Determine if a change to the training matrix is needed'
                  /> */}
                  <InlineHtml
                    name='comments'
                    label={useMarsbioForm ? 'Other Comments' : 'Comments'}
                    disabled={isFieldDisabled('comments')}
                    helperText='Provide any additional information here'
                    attachments={changeOrder.task.attachments || []}
                    onChangeAttachments={handleOnChangeAttachment}
                  />
                </>
              ) : (
                <>
                  <InlineDatePicker
                    name='requestDate'
                    label='Request Date'
                    disabled={isFieldDisabled('requestDate')}
                    autoCloseOnSelect={true}
                  />
                  <InlineFormControl label='Requestor'>
                    <SingleUserSelector
                      value={currentRequestor || undefined}
                      textFieldProps={{
                        label: undefined,
                        placeholder: 'Select a user to assign as the Requestor',
                        helperText: useCustomfields ? 'Change Requestor' : ''
                      }}
                      disabled={isFieldDisabled('requestor')}
                      onChange={(user: User | null) => {
                        setCurrentRequestor(user?.id || null);
                        setFieldValue('requestor', user?.id || null, true);
                        submitForm();
                      }}
                    />
                  </InlineFormControl>
                  <InlineTextField
                    name='impactOfChange'
                    label='Impact of Change'
                    disabled={isFieldDisabled('impactOfChange')}
                    multiline
                  />
                </>
              )}
            </InlineForm>
          </CardContent>
        </Card>
      )}
    </InlineFormik>
  );
};
